import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import {
  canRevealKhushiItinerary,
  fetchItineraryConfig,
  isItemRevealedFromItem,
  itineraryItemsFromDoc,
} from "./khushiAccess";
import { KHUSHI_ITINERARY, type KhushiItineraryItem } from "./khushiData";
import { isOwnerEmail } from "../ownerConfig";
import { injectKhushiFonts, KHUSHI_COLORS, KHUSHI_FONTS } from "./theme";
import { fetchNycWeather, WeatherStrip, type WeatherState } from "./components/WeatherStrip";
import { ItineraryEventItem } from "./components/ItineraryEventItem";

const { maroon, maroonMid, gold, goldSoft, ink } = KHUSHI_COLORS;
const { serif: fontSerif, display: fontDisplay, script: fontScript } = KHUSHI_FONTS;

const sxRoot: Record<string, unknown> = {
  minHeight: "100vh",
  boxSizing: "border-box",
  px: { xs: 2.5, sm: 3 },
  pt: 4,
  pb: 6,
  background: `radial-gradient(ellipse 120% 80% at 50% -20%, rgba(212, 175, 55, 0.12), transparent 55%), linear-gradient(165deg, ${maroonMid} 0%, ${maroon} 45%)`,
  color: ink,
  fontFamily: fontSerif,
  fontSize: "1.2rem",
  lineHeight: 1.45,
  "&, & *::before, & *::after": { boxSizing: "border-box" },
};

const sxWeatherStrip = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "baseline",
  justifyContent: "center",
  gap: "0.35rem 0.75rem",
  mb: 4,
  py: 1.1,
  px: 2,
  border: `1px solid rgba(212, 175, 55, 0.22)`,
  borderRadius: "2px",
  bgcolor: "rgba(0,0,0,0.2)",
  fontFamily: fontDisplay,
  fontSize: "0.8rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: goldSoft,
  "& strong": {
    color: gold,
    fontWeight: 600,
    letterSpacing: "0.08em",
  },
};

const sxTimeline: Record<string, unknown> = {
  position: "relative",
  m: 0,
  pl: 2.5,
  listStyle: "none",
  "&::before": {
    content: '""',
    position: "absolute",
    left: "0.35rem",
    top: "0.35rem",
    bottom: "0.35rem",
    width: "1px",
    background: `linear-gradient(${gold}, rgba(212, 175, 55, 0.15) 90%)`,
  },
};

function KhushiItinerary() {
  const [accessGranted, setAccessGranted] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [configReady, setConfigReady] = useState(false);
  const [itineraryItems, setItineraryItems] = useState<
    KhushiItineraryItem[] | null
  >(null);
  const [tick, setTick] = useState(0);
  const [weather, setWeather] = useState<WeatherState>({ status: "idle" });
  const previouslyRevealedRef = useRef<Set<string>>(new Set());
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    injectKhushiFonts();
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (user) => {
      setAccessGranted(canRevealKhushiItinerary(user?.email ?? null));
      setIsOwner(isOwnerEmail(user?.email ?? null));
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setConfigReady(false);
    fetchItineraryConfig()
      .then((docValue) => {
        if (cancelled) return;
        if (docValue) setItineraryItems(itineraryItemsFromDoc(docValue));
        else setItineraryItems(KHUSHI_ITINERARY);
      })
      .catch(() => {
        if (!cancelled) setItineraryItems(KHUSHI_ITINERARY);
      })
      .finally(() => {
        if (!cancelled) setConfigReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isPrivileged = isOwner || accessGranted;

  const anyPublicRevealed = useMemo(() => {
    const items = itineraryItems ?? KHUSHI_ITINERARY;
    return items.some((item) => isItemRevealedFromItem(item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryItems, tick]);

  const anyRevealed = isPrivileged || anyPublicRevealed;

  useEffect(() => {
    if (isPrivileged) return;
    const items = itineraryItems ?? null;
    if (!items) return;
    const nowRevealed = new Set<string>();
    const newlyRevealed: string[] = [];

    for (const item of items) {
      const revealed = isItemRevealedFromItem(item);
      if (revealed) {
        nowRevealed.add(item.id);
        if (!previouslyRevealedRef.current.has(item.id))
          newlyRevealed.push(item.id);
      }
    }

    previouslyRevealedRef.current = nowRevealed;
    if (newlyRevealed.length === 0) return;

    setAnimatingIds((prev) => {
      const next = new Set(prev);
      newlyRevealed.forEach((id) => next.add(id));
      return next;
    });

    const id = window.setTimeout(() => {
      setAnimatingIds((prev) => {
        const next = new Set(prev);
        newlyRevealed.forEach((rid) => next.delete(rid));
        return next;
      });
    }, 1000);

    return () => window.clearTimeout(id);
  }, [isPrivileged, itineraryItems, tick]);

  useEffect(() => {
    if (isPrivileged) return;
    const items = itineraryItems ?? null;
    if (!items) return;

    const now = Date.now();
    let nextTs: number | null = null;

    for (const item of items) {
      const raw = item.revealAt;
      if (!raw) continue;
      const ts = new Date(raw).getTime();
      if (Number.isNaN(ts) || ts <= now) continue;
      if (nextTs === null || ts < nextTs) nextTs = ts;
    }

    if (nextTs === null) return;

    const delay = Math.max(0, Math.min(nextTs - now, 2_147_483_647));
    const id = window.setTimeout(() => setTick(Date.now()), delay);
    return () => window.clearTimeout(id);
  }, [isPrivileged, itineraryItems]);

  useEffect(() => {
    let cancelled = false;
    setWeather({ status: "loading" });
    fetchNycWeather()
      .then((w) => {
        if (!cancelled) setWeather({ status: "ok", ...w });
      })
      .catch(() => {
        if (!cancelled) setWeather({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const weatherBlock = useMemo(
    () => <WeatherStrip sx={sxWeatherStrip} weather={weather} />,
    [weather],
  );

  return (
    <Box sx={sxRoot}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          maxWidth: "42rem",
          mx: "auto",
          mb: 3,
          fontFamily: fontDisplay,
          fontSize: "0.75rem",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: goldSoft,
          opacity: 0.85,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: fontDisplay,
            fontSize: "inherit",
            letterSpacing: "inherit",
            textTransform: "inherit",
            color: "inherit",
          }}
        >
          A day in the city
        </Typography>
        <Link
          component={RouterLink}
          to="/home"
          underline="none"
          sx={{
            fontFamily: fontDisplay,
            fontSize: "inherit",
            letterSpacing: "inherit",
            textTransform: "inherit",
            color: goldSoft,
            borderBottom: "1px solid rgba(232, 213, 163, 0.35)",
            pb: "2px",
            "&:hover": { color: gold, borderBottomColor: gold },
          }}
        >
          Home
        </Link>
      </Box>

      <Box
        component="article"
        sx={{
          maxWidth: "42rem",
          mx: "auto",
          p: "2rem 1.75rem 2.25rem",
          border: "1px solid rgba(212, 175, 55, 0.35)",
          borderRadius: "4px",
          background: `linear-gradient(145deg, rgba(74, 21, 34, 0.55) 0%, rgba(20, 6, 10, 0.72) 100%)`,
          boxShadow:
            "0 24px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
        }}
      >
        <Typography
          sx={{
            textAlign: "center",
            color: gold,
            fontFamily: fontDisplay,
            letterSpacing: "0.35em",
            fontSize: "0.85rem",
            mb: 1.5,
          }}
        >
          ✦ ✦ ✦
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontFamily: fontScript,
            fontWeight: 400,
            fontSize: { xs: "2.25rem", sm: "clamp(2.25rem, 5vw, 3.1rem)" },
            textAlign: "center",
            letterSpacing: "0.02em",
            color: gold,
            m: 0,
            mb: 1,
            lineHeight: 1.2,
            textShadow:
              "0 10px 30px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.08)",
          }}
        >
          For Khushi
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            fontStyle: "italic",
            color: goldSoft,
            m: 0,
            mb: 3,
            fontSize: "1.1rem",
          }}
        >
          A little something, when the day comes. <br /> Happy Birthday
          Beautiful
        </Typography>

        {!anyRevealed && (
          <Typography
            sx={{
              textAlign: "center",
              m: 0,
              mb: 3.5,
              px: 0.5,
            }}
          >
            Something is waiting in New York — not random, not rushed, just for
            you. The when and where stay tucked away until you are meant to see
            them.
          </Typography>
        )}

        {anyRevealed && (
          <Typography sx={{ textAlign: "center", m: 0, mb: 2.5, px: 0.5 }}>
            Here is the plan we sketched — tap the links when you are ready to
            peek ahead.
          </Typography>
        )}

        {isOwner && (
          <Box
            sx={{
              mb: 2.5,
              py: 1.25,
              px: 1.5,
              border: "1px dashed rgba(212, 175, 55, 0.45)",
              borderRadius: "4px",
              bgcolor: "rgba(0,0,0,0.22)",
            }}
          >
            <Typography
              sx={{
                fontFamily: fontDisplay,
                fontSize: "0.68rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: gold,
                mb: 0.5,
              }}
            >
              Planner view
            </Typography>
            <Typography
              sx={{
                fontSize: "0.95rem",
                color: goldSoft,
                fontStyle: "italic",
                m: 0,
              }}
            >
              Confirmation links and booking notes below are only visible when
              you are signed in as the site owner.
            </Typography>
          </Box>
        )}

        {weatherBlock}

        <Box component="ol" sx={sxTimeline}>
          {(itineraryItems ?? KHUSHI_ITINERARY).map((item) => (
            <ItineraryEventItem
              key={item.id}
              item={item}
              isPrivileged={isPrivileged}
              isOwner={isOwner}
              isAnimating={!isPrivileged && animatingIds.has(item.id)}
            />
          ))}
        </Box>

        <Typography
          sx={{
            mt: 4,
            textAlign: "center",
            fontSize: "0.98rem",
            color: "rgba(248, 240, 230, 0.65)",
            fontStyle: "italic",
            "& a": { color: goldSoft },
          }}
        >
          {!authReady ? (
            "…"
          ) : !configReady ? (
            "…"
          ) : anyRevealed ? (
            "If anything shifts, we will adjust together."
          ) : (
            <>
              Some details unlock over time. If you have access, sign in on{" "}
              <Link
                component={RouterLink}
                to="/home"
                sx={{ color: "inherit", fontStyle: "italic" }}
              >
                Home → Log-in
              </Link>
              , then return here.
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

export default KhushiItinerary;
